import React, { useState, useEffect, useCallback, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { UserContext } from '../../context/UserContext';
import './GiveKudos.css';
import KudosIcon from '../../img/icons/Kudos_given.png';

// Rainbow animal avatars for privacy protection
const animalAvatars = [
  { name: 'Red', emoji: '🦊', color: '#FF6B6B' },
  { name: 'Orange', emoji: '🐯', color: '#FF9F43' },
  { name: 'Yellow', emoji: '🐥', color: '#FECA57' },
  { name: 'Green', emoji: '🐸', color: '#7BC67B' },
  { name: 'Blue', emoji: '🐳', color: '#54A0FF' },
  { name: 'Indigo', emoji: '🦋', color: '#5F6FF5' },
  { name: 'Violet', emoji: '🦄', color: '#A55EEA' },
];

const PRIVACY_THRESHOLD = 50;

/**
 * GiveKudos Component
 *
 * Displays event kudos/recognition with the same privacy protection as attendees.
 * Shows FB respondents count and Move & Muse confirmed attendees who received kudos.
 * When 50+ attendees, the count bubble becomes clickable to view attendees & give kudos.
 */
const GiveKudos = ({
  eventId,
  isPastEvent = false,
  refreshKey = 0,
  apiBase = process.env.REACT_APP_API_URL || 'http://localhost:5000/api/v1'
}) => {
  const navigate = useNavigate();
  const { user } = useContext(UserContext);
  const [attendeeData, setAttendeeData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showTooltip, setShowTooltip] = useState(false);

  // Fetch attendee data (reuses the same endpoint)
  const fetchAttendees = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      const response = await fetch(
        `${apiBase}/events/attendees/${eventId}`,
        { headers }
      );
      const data = await response.json();

      if (data.success) {
        setAttendeeData(data.data);
      }
    } catch (error) {
      console.error('Error fetching attendees:', error);
    } finally {
      setLoading(false);
    }
  }, [eventId, apiBase]);

  useEffect(() => {
    fetchAttendees();
  }, [fetchAttendees, refreshKey]);

  // Get animal avatar for placeholder
  const getAnimalAvatar = (index) => {
    return animalAvatars[index % animalAvatars.length];
  };

  if (loading) {
    return (
      <div className="give-kudos-section loading">
        <div className="spinner-small"></div>
      </div>
    );
  }

  if (!attendeeData) return null;

  const {
    confirmedCount,
    attendees,
    canExpandList,
    privacyThreshold,
    userConfirmed
  } = attendeeData;

  // Calculate count display for 8th bubble
  // If <= 7 people, show just the number; otherwise show +remaining
  const getCountDisplay = () => {
    if (confirmedCount <= 7) {
      return confirmedCount.toString();
    }
    return `+${confirmedCount - 7}`;
  };

  // Countdown to list reveal
  const confirmationsNeeded = Math.max(0, PRIVACY_THRESHOLD - confirmedCount);

  // Check if user can view attendees page (threshold met AND confirmed attendance)
  const canViewAttendeesPage = confirmedCount >= PRIVACY_THRESHOLD && userConfirmed;

  // Handle click on count bubble
  const handleCountBubbleClick = () => {
    if (canViewAttendeesPage) {
      navigate(`/event/${eventId}/attendees`);
    } else if (!user) {
      navigate('/login');
    }
  };

  return (
    <section className="give-kudos-section">
      <div className="kudos-header">
        <img src={KudosIcon} alt="Kudos" className="kudos-icon" />
        <h3>Give Kudos</h3>
      </div>

      {/* Attendee Grid - 2 rows of 4 (7 avatars + 1 count bubble) */}
      <div className="kudos-grid">
        {/* Show up to 7 animal avatars based on confirmed count */}
        {Array.from({ length: Math.min(confirmedCount, 7) }).map((_, index) => {
          const animal = getAnimalAvatar(index);
          const attendee = attendees?.[index];
          const isRevealed = canExpandList && attendee && !attendee.isPlaceholder;

          return (
            <div key={index} className="kudos-item">
              <div
                className="kudos-avatar"
                style={!isRevealed ? { backgroundColor: animal.color } : {}}
              >
                {isRevealed && attendee.profilePicture ? (
                  <img src={attendee.profilePicture} alt={attendee.name} />
                ) : isRevealed ? (
                  <span className="avatar-initial">
                    {attendee.name?.charAt(0)?.toUpperCase() || '?'}
                  </span>
                ) : (
                  <span className="animal-emoji">{animal.emoji}</span>
                )}
              </div>
              <span className="kudos-name">
                {isRevealed ? attendee.name : animal.name}
              </span>
            </div>
          );
        })}

        {/* Count bubble - always in 8th position, clickable when threshold met */}
        <div className="kudos-item count-bubble">
          <div
            className={`kudos-avatar count-avatar ${canViewAttendeesPage ? 'clickable' : ''}`}
            onMouseEnter={() => setShowTooltip(true)}
            onMouseLeave={() => setShowTooltip(false)}
            onClick={handleCountBubbleClick}
          >
            <span>{getCountDisplay()}</span>
          </div>
          <span className="kudos-name">people</span>

          {/* Tooltip */}
          {showTooltip && (
            <div className="kudos-tooltip">
              {canViewAttendeesPage ? (
                'Click to view attendees and give kudos!'
              ) : confirmedCount >= PRIVACY_THRESHOLD && !userConfirmed ? (
                'Confirm your attendance to give kudos'
              ) : (
                'To protect community privacy, the Kudos feature activates once 50 attendees confirm their participation.'
              )}
            </div>
          )}
        </div>
      </div>

      {/* Countdown Notice or Ready Notice */}
      {confirmationsNeeded > 0 ? (
        <p className="countdown-notice">
          <i className="bi bi-lock"></i>
          {confirmationsNeeded} confirmation{confirmationsNeeded !== 1 ? 's' : ''} before list reveal
        </p>
      ) : !userConfirmed ? (
        <p className="countdown-notice confirm-needed">
          <i className="bi bi-check2-circle"></i>
          Confirm attendance to give kudos
        </p>
      ) : (
        <button className="btn-view-attendees" onClick={handleCountBubbleClick}>
          <i className="bi bi-hand-thumbs-up-fill"></i>
          Give Kudos to Attendees
        </button>
      )}
    </section>
  );
};

export default GiveKudos;
